import {useCallback, useRef, useState} from 'react';

/**
 * El gesto del mazo: arrastrar una carta a la derecha aprueba, a la izquierda rechaza.
 *
 * Los números son los del mockup (`dashbo-ia-booster-con-barra-lateral_9.html`), no aproximaciones:
 * están calibrados contra el tamaño de la carta y cambiarlos cambia cuánto hay que arrastrar para
 * que el gesto "se sienta" decidido. Se exportan para que el CSS y los tests hablen del mismo
 * umbral que el gesto.
 *
 * Tres cosas que el mockup hace mal y acá NO:
 *
 * 1. **Pointer events, no mouse + touch por separado.** El mockup escucha `mousedown` y
 *    `touchstart` y cuelga `mousemove`/`mouseup` del `document` para siempre. Con
 *    `setPointerCapture` el navegador nos manda los eventos aunque el dedo salga de la carta, y el
 *    listener muere con el componente. Además cubre lápiz/trackpad de un saque.
 *
 * 2. **`preventDefault` SÓLO si el movimiento es más horizontal que vertical.** Cancelarlo siempre
 *    mata el scroll vertical de la página en touch: el dedo queda preso de la carta y la única
 *    forma de bajar es soltarla. Junto con `touch-action: pan-y` en el CSS, el eje vertical sigue
 *    siendo de la página.
 *
 * 3. **Un `<button>` de la carta no arranca un arrastre.** "Editar", "Fusionar y aprobar" o el link
 *    a la fuente están DENTRO del área que se arrastra; sin este guard, apretar uno mueve la carta.
 */

/** Cuánto hay que arrastrar (px) para que soltar decida en vez de volver al centro. */
export const SWIPE_TH = 95;

/** Grados de rotación por píxel arrastrado. La carta "pivota" como una carta física. */
export const ROTATION_PER_PX = 0.055;

/** El eje Y va amortiguado a 1/4: la carta acompaña el dedo sin escaparse hacia arriba. */
export const Y_DAMPING = 0.25;

/** Desde acá el borde de la carta se tiñe del color de la decisión. */
export const TILT_FROM = 18;

/** Desde acá se ilumina el nub lateral (la pastilla Aprobar/Rechazar de la arena). */
export const NUB_FROM = 30;

/** Duración de la salida (la carta se va volando). Espeja el `.33s` del CSS. */
export const FLY_MS = 330;

/** Con `prefers-reduced-motion` no hay vuelo: un fade y listo. */
export const REDUCED_FLY_MS = 120;

/** Selector de lo que NO arranca un arrastre aunque esté dentro de la carta. */
const INTERACTIVE = 'button, a, textarea, input, select, [role="button"]';

export function prefersReducedMotion() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
        return !!window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
        // Algún navegador viejo (y jsdom según versión) tira al evaluar la media query.
        return false;
    }
}

/** El transform de la carta mientras se arrastra. Y amortiguado + rotación proporcional. */
export function swipeTransform(dx, dy) {
    return `translate(${dx}px, ${dy * Y_DAMPING}px) rotate(${dx * ROTATION_PER_PX}deg)`;
}

/**
 * Opacidad del sello (APROBAR / RECHAZAR) para un desplazamiento dado.
 * Llega a 1 exactamente en el umbral: el sello lleno ES la señal de que soltar ya decide.
 */
export function stampOpacity(dx, direction) {
    if (direction === 'right' && dx <= 0) return 0;
    if (direction === 'left' && dx >= 0) return 0;
    return Math.min(1, Math.abs(dx) / SWIPE_TH);
}

/** `'right'` | `'left'` | `null` — qué decidiría soltar acá. */
export function swipeDecision(dx) {
    if (dx > SWIPE_TH) return 'right';
    if (dx < -SWIPE_TH) return 'left';
    return null;
}

/**
 * @param enabled    la carta es la de arriba, no está en edición y no hay una salida en curso.
 * @param onSwipe    (direction) => void, cuando se suelta cruzando el umbral.
 * @param onDragMove (dx) => void en cada movimiento y 0 al soltar. Lo usa la arena para encender
 *                   los nubs SIN re-renderizar las tres cartas en cada frame.
 */
export function useSwipeCard({enabled = true, onSwipe, onDragMove} = {}) {
    const [drag, setDrag] = useState(null);
    // El estado del gesto en curso. Ref y no estado: se lee dentro de los handlers y no debe
    // arrastrar un render de por medio.
    const startRef = useRef(null);

    const report = useCallback((dx) => {
        if (onDragMove) onDragMove(dx);
    }, [onDragMove]);

    const release = useCallback((node, pointerId) => {
        if (node && typeof node.releasePointerCapture === 'function' && pointerId !== undefined) {
            try {
                node.releasePointerCapture(pointerId);
            } catch (e) {
                // El pointer ya se soltó (o nunca se capturó): no hay nada que devolver.
            }
        }
    }, []);

    const onPointerDown = useCallback((e) => {
        if (!enabled) return;
        if (e.target && typeof e.target.closest === 'function' && e.target.closest(INTERACTIVE)) return;
        // Sólo el botón principal: un click derecho no arrastra cartas.
        if (typeof e.button === 'number' && e.button > 0) return;

        startRef.current = {x: e.clientX, y: e.clientY, pointerId: e.pointerId, dx: 0, dy: 0};
        const node = e.currentTarget;
        if (node && typeof node.setPointerCapture === 'function' && e.pointerId !== undefined) {
            try {
                node.setPointerCapture(e.pointerId);
            } catch (e2) {
                // Sin captura el gesto sigue andando mientras el puntero no salga de la carta.
            }
        }
        setDrag({dx: 0, dy: 0});
    }, [enabled]);

    const onPointerMove = useCallback((e) => {
        const start = startRef.current;
        if (!start) return;
        if (e.pointerId !== undefined && start.pointerId !== undefined
            && e.pointerId !== start.pointerId) return;

        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        start.dx = dx;
        start.dy = dy;
        if (Math.abs(dx) > Math.abs(dy) && e.cancelable) e.preventDefault();
        setDrag({dx, dy});
        report(dx);
    }, [report]);

    const onPointerUp = useCallback((e) => {
        const start = startRef.current;
        if (!start) return;
        startRef.current = null;
        release(e.currentTarget, e.pointerId);
        // El delta que decide es el del último movimiento, no el del `pointerup`: son el mismo punto
        // en la práctica, y así el gesto no depende de que el evento de soltar traiga coordenadas.
        const decision = swipeDecision(start.dx);
        setDrag(null);
        report(0);
        if (decision && onSwipe) onSwipe(decision);
    }, [release, onSwipe, report]);

    const onPointerCancel = useCallback((e) => {
        startRef.current = null;
        release(e.currentTarget, e.pointerId);
        setDrag(null);
        report(0);
    }, [release, report]);

    return {
        dragging: drag !== null,
        dx: drag ? drag.dx : 0,
        dy: drag ? drag.dy : 0,
        handlers: {onPointerDown, onPointerMove, onPointerUp, onPointerCancel},
    };
}

export default useSwipeCard;
